(() => {
  'use strict';
  if (window.AWModelContextBridge?.version) return;

  const VERSION = '1.0.2-cwv-20261002';
  const KEYS = {
    context: 'aw_airsoft_context_v1',
    exact: 'aw_exact_model_context_v1',
    configurator: 'aw_airsoft_configurator',
    replicas: 'aw_garage_replicas_v1',
    active: 'aw_garage_active_replica_v1',
    saved: 'aw_airsoft_saved_configurations',
    modelMap: 'aw_saved_config_models_v1',
    history: 'aw_airsoft_purchase_history'
  };

  const normalize = (value = '') => String(value)
    .replace(/[₂²]/g, '2').replace(/[₀⁰]/g, '0')
    .trim().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' et ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const readJson = (key, fallback) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; }
    catch (_) { return fallback; }
  };
  const writeJson = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };
  const writeText = (key, value) => {
    try { if (value) localStorage.setItem(key, value); else localStorage.removeItem(key); } catch (_) {}
  };
  const track = (name, payload = {}) => {
    try { window.AWAnalytics?.track?.(name, payload); } catch (_) {}
  };

  // Read live fields on every lookup; reuse normalization only while every
  // canonical field is unchanged. Replaced/edited source nodes invalidate themselves.
  const nodeRecords = new WeakMap();
  const normalizedRecords = new WeakMap();
  const recordFromNode = (node) => {
    if (!node) return null;
    const fields = ['handle', 'label', 'model', 'manufacturer', 'platform', 'platformLabel', 'type', 'pageUrl'];
    const values = fields.map((field) => String(node.dataset[field] || ''));
    const previous = nodeRecords.get(node);
    if (previous && values.every((value, index) => value === previous.values[index])) return previous.record;
    const record = Object.fromEntries(fields.map((field, index) => [field, values[index].trim()]));
    normalizedRecords.set(record, {
      key: normalize(node.dataset.handle || node.dataset.label || node.dataset.model || ''),
      candidates: [record.handle, record.label, record.model].map(normalize).filter(Boolean),
      manufacturer: normalize(record.manufacturer)
    });
    nodeRecords.set(node, { values, record });
    return record;
  };

  const modelRecords = () => {
    const seen = new Set();
    return [...document.querySelectorAll('[data-aw-tool-model],[data-aw-model-context-model]')]
      .map(recordFromNode).filter((record) => {
        const key = normalizedRecords.get(record).key;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const stripManufacturer = (value = '', manufacturer = '') => {
    let result = normalize(value);
    const maker = normalize(manufacturer);
    if (maker && result.startsWith(`${maker}-`)) result = result.slice(maker.length + 1);
    return result;
  };

  const baseMatches = (record, context = {}) => {
    if (!record) return false;
    const platform = normalize(context.platform || '');
    const recordPlatform = normalize(record.platform || record.platformLabel || '');
    if (platform && recordPlatform && platform !== recordPlatform && platform !== normalize(record.platformLabel || '')) return false;
    const manufacturer = normalize(context.manufacturer || '');
    const recordManufacturer = normalize(record.manufacturer || '');
    if (manufacturer && recordManufacturer && manufacturer !== recordManufacturer) return false;
    const type = normalize(context.type || context.replicaType || '');
    const recordType = normalize(record.type || '');
    if (type && recordType && type !== recordType) return false;
    return true;
  };

  const modelTextMatches = (record, value = '', manufacturer = '', preparedNeedle) => {
    if (!record || !value) return false;
    const needle = preparedNeedle === undefined ? normalize(value) : preparedNeedle;
    const cached = normalizedRecords.get(record);
    const candidates = cached?.candidates || [record.handle, record.label, record.model].map(normalize).filter(Boolean);
    if (candidates.includes(needle)) return true;
    const maker = normalize(manufacturer || record.manufacturer);
    const strip = (text) => maker && text.startsWith(`${maker}-`) ? text.slice(maker.length + 1) : text;
    const strippedNeedle = strip(needle);
    return candidates.some((candidate) => strip(candidate) === strippedNeedle);
  };

  const findModel = (value = '', context = {}) => {
    const needle = normalize(value);
    if (!needle) return null;
    const matches = modelRecords()
      .filter(Boolean)
      .filter((record) => modelTextMatches(record, value, context.manufacturer || record.manufacturer, needle))
      .filter((record) => baseMatches(record, context));
    if (matches.length === 1) return matches[0];

    const exactHandle = matches.find((record) => normalize(record.handle) === needle);
    if (exactHandle) return exactHandle;

    const exactLabel = matches.filter((record) => normalize(record.label) === needle);
    return exactLabel.length === 1 ? exactLabel[0] : null;
  };

  const recordForCurrentPage = () => {
    const path = location.pathname.replace(/\/+$/, '');
    const match = modelRecords().find((record) => {
      if (!record?.pageUrl) return false;
      try { return new URL(record.pageUrl, location.origin).pathname.replace(/\/+$/, '') === path; }
      catch (_) { return false; }
    });
    return match || null;
  };

  const activeReplica = () => {
    const id = String(readJson(KEYS.active, '') || '');
    const replicas = readJson(KEYS.replicas, []);
    if (!id || !Array.isArray(replicas)) return null;
    return replicas.find((replica) => String(replica?.id || '') === id) || null;
  };

  const resolveReplica = (replica) => {
    if (!replica) return null;
    const context = {
      platform: replica.platform || '',
      manufacturer: replica.manufacturer || '',
      type: replica.type || ''
    };
    return findModel(replica.model || '', context);
  };

  const preservedExactForReplica = (replica) => {
    if (!replica) return null;
    const existing = readJson(KEYS.exact, {});
    if (!existing || existing.status !== 'exact' || !existing.handle) return null;
    if (!baseMatches(existing, replica)) return null;
    if (replica.model && !modelTextMatches(existing, replica.model, replica.manufacturer || existing.manufacturer || '')) return null;
    return {
      ...existing,
      source: 'garage-exact-preserved',
      garageReplicaId: String(replica.id || existing.garageReplicaId || ''),
      garageReplicaLabel: String(replica.nickname || replica.model || existing.garageReplicaLabel || ''),
      garageModel: String(replica.model || existing.garageModel || ''),
      updatedAt: new Date().toISOString()
    };
  };

  const exactRecord = (record, source, replica = null) => ({
    status: 'exact',
    source,
    handle: String(record.handle || ''),
    label: String(record.label || record.model || ''),
    model: String(record.model || record.label || ''),
    platform: normalize(record.platform || ''),
    platformLabel: String(record.platformLabel || record.platform || ''),
    manufacturer: normalize(record.manufacturer || ''),
    manufacturerLabel: String(record.manufacturer || ''),
    type: normalize(record.type || ''),
    typeLabel: String(record.type || ''),
    garageReplicaId: replica ? String(replica.id || '') : '',
    garageReplicaLabel: replica ? String(replica.nickname || replica.model || '') : '',
    garageModel: replica ? String(replica.model || '') : '',
    updatedAt: new Date().toISOString()
  });

  const unresolvedRecord = (replica, source = 'garage-unresolved') => ({
    status: replica?.model ? 'unresolved' : 'none',
    source,
    handle: '',
    label: '',
    model: '',
    platform: normalize(replica?.platform || ''),
    platformLabel: String(replica?.platform || ''),
    manufacturer: normalize(replica?.manufacturer || ''),
    manufacturerLabel: String(replica?.manufacturer || ''),
    type: normalize(replica?.type || ''),
    typeLabel: String(replica?.type || ''),
    garageReplicaId: String(replica?.id || ''),
    garageReplicaLabel: String(replica?.nickname || replica?.model || ''),
    garageModel: String(replica?.model || ''),
    updatedAt: new Date().toISOString()
  });

  const saveExactContext = (record) => {
    if (!record) return null;
    const previous = readJson(KEYS.exact, {});
    const signature = [
      record.status, record.handle, record.label, record.platform, record.manufacturer, record.type,
      record.garageReplicaId, record.garageModel
    ].map(String).join('|');
    const previousSignature = [
      previous.status, previous.handle, previous.label, previous.platform, previous.manufacturer, previous.type,
      previous.garageReplicaId, previous.garageModel
    ].map(String).join('|');
    if (signature !== previousSignature) writeJson(KEYS.exact, record);
    return record;
  };

  const applyToSharedContext = (record) => {
    if (!record) return;
    const previous = readJson(KEYS.context, {});
    const next = {
      ...previous,
      version: Math.max(Number(previous.version || 1), 2),
      platform: record.platform || normalize(previous.platform || ''),
      platformLabel: record.platformLabel || previous.platformLabel || '',
      manufacturer: record.manufacturer || '',
      manufacturerLabel: record.manufacturerLabel || '',
      type: record.type || '',
      typeLabel: record.typeLabel || '',
      model: record.status === 'exact' ? record.model : '',
      modelLabel: record.status === 'exact' ? record.label : '',
      modelHandle: record.status === 'exact' ? record.handle : '',
      modelStatus: record.status,
      garageModel: record.garageModel || '',
      garageReplicaId: record.garageReplicaId || '',
      garageReplicaLabel: record.garageReplicaLabel || '',
      source: record.source || 'model-context-bridge',
      updatedAt: new Date().toISOString()
    };
    const relevant = (value) => [
      value.platform, value.manufacturer, value.type, value.model, value.modelLabel, value.modelHandle,
      value.modelStatus, value.garageModel, value.garageReplicaId
    ].map(String).join('|');
    const changed = relevant(previous) !== relevant(next);
    if (changed) writeJson(KEYS.context, next);
    writeText('aw_selected_platform', next.platform);
    writeText('aw_selected_manufacturer', next.manufacturer);
    writeText('aw_selected_replica_type', next.type);

    const state = readJson(KEYS.configurator, {});
    if (state && typeof state === 'object') {
      const stateBase = {
        platform: normalize(state.platform || ''),
        manufacturer: normalize(state.manufacturer || ''),
        type: normalize(state.type || '')
      };
      const recordBase = {
        platform: record.platform,
        manufacturer: record.manufacturer,
        type: record.type
      };
      const samePlatform = !stateBase.platform || stateBase.platform === recordBase.platform;
      if (samePlatform || /\/pages\/configurateur-airsoft\/?$/.test(location.pathname)) {
        state.platform = record.platform || state.platform || '';
        if (record.platformLabel) state.platformLabel = record.platformLabel;
        state.manufacturer = record.manufacturer || '';
        state.type = record.type || '';
        state.model = record.status === 'exact' ? record.model : '';
        state.modelLabel = record.status === 'exact' ? record.label : '';
        state.modelHandle = record.status === 'exact' ? record.handle : '';
        if (!baseMatches(record, stateBase) && Array.isArray(state.selected) && state.selected.length) {
          state.contextNeedsReview = true;
        }
        writeJson(KEYS.configurator, state);
      }
    }

    if (changed) {
      try { window.dispatchEvent(new CustomEvent('aw:context-changed', { detail: next })); } catch (_) {}
      track('model_context_changed', {
        model_status: record.status,
        model_handle: record.handle || '',
        platform: record.platform || '',
        source: record.source || ''
      });
    }
  };

  const explicitContext = () => {
    const params = new URLSearchParams(location.search);
    const requested = params.get('aw_model') || '';
    if (requested) {
      const record = findModel(requested, {});
      return { blocked: !record, record, source: 'url-exact-model' };
    }
    const page = recordForCurrentPage();
    if (page) return { blocked: false, record: page, source: 'exact-model-page' };
    if (params.has('aw_platform') || params.has('aw_config')) return { blocked: true, record: null, source: 'explicit-non-garage-context' };
    return { blocked: false, record: null, source: '' };
  };

  const syncContext = () => {
    const explicit = explicitContext();
    if (explicit.record) {
      const record = saveExactContext(exactRecord(explicit.record, explicit.source));
      applyToSharedContext(record);
      return record;
    }
    if (explicit.blocked) return null;

    const replica = activeReplica();
    if (!replica?.id || !normalize(replica.platform || '')) return null;
    const canonical = modelRecords().length ? resolveReplica(replica) : preservedExactForReplica(replica);
    const record = canonical
      ? (canonical.status === 'exact' ? { ...canonical, updatedAt: new Date().toISOString() } : exactRecord(canonical, 'garage-exact-model', replica))
      : unresolvedRecord(replica);
    saveExactContext(record);
    applyToSharedContext(record);

    if (
      record.status === 'exact' &&
      /\/pages\/configurateur-airsoft\/?$/.test(location.pathname) &&
      !new URLSearchParams(location.search).has('aw_model')
    ) {
      const url = new URL(location.href);
      url.searchParams.set('aw_model', record.handle);
      history.replaceState({}, '', url);
    }
    return record;
  };

  const syncFromShared = () => {
    const shared = readJson(KEYS.context, {});
    const canonical = findModel(shared.modelHandle || shared.modelLabel || shared.model || '', shared);
    if (!canonical) return null;
    const record = saveExactContext(exactRecord(canonical, shared.source || 'shared-exact-model'));
    return record;
  };

  const currentExact = () => {
    const record = readJson(KEYS.exact, {});
    if (!record || record.status !== 'exact' || !record.handle) return null;
    const shared = readJson(KEYS.context, {});
    if (shared.platform && normalize(shared.platform) !== normalize(record.platform)) return null;
    if (shared.manufacturer && record.manufacturer && normalize(shared.manufacturer) !== normalize(record.manufacturer)) return null;
    if (shared.type && record.type && normalize(shared.type) !== normalize(record.type)) return null;
    return record;
  };

  const directBodyContext = (body) => {
    const property = (name) => {
      if (body instanceof FormData || body instanceof URLSearchParams) return String(body.get(`properties[${name}]`) || '');
      return '';
    };
    if (body instanceof FormData || body instanceof URLSearchParams) {
      return {
        platform: property('Plateforme AW') || property('_AW Platform'),
        manufacturer: property('Fabricant AW') || property('_AW Manufacturer'),
        type: property('Type de réplique AW') || property('_AW Replica Type'),
        model: property('Réplique exacte AW') || property('_AW Model'),
        modelHandle: property('_AW Model Handle')
      };
    }
    if (typeof body !== 'string') return {};
    try {
      const parsed = JSON.parse(body);
      const props = Array.isArray(parsed?.items) ? (parsed.items[0]?.properties || {}) : (parsed?.properties || {});
      return {
        platform: props['Plateforme AW'] || props['_AW Platform'] || '',
        manufacturer: props['Fabricant AW'] || props['_AW Manufacturer'] || '',
        type: props['Type de réplique AW'] || props['_AW Replica Type'] || '',
        model: props['Réplique exacte AW'] || props['_AW Model'] || '',
        modelHandle: props['_AW Model Handle'] || ''
      };
    } catch (_) { return {}; }
  };

  const contextMatchesRecord = (record, direct = {}) => {
    if (!record) return false;
    if (direct.platform && normalize(direct.platform) !== normalize(record.platform) && normalize(direct.platform) !== normalize(record.platformLabel)) return false;
    if (direct.manufacturer && record.manufacturer && normalize(direct.manufacturer) !== normalize(record.manufacturer)) return false;
    if (direct.type && record.type && normalize(direct.type) !== normalize(record.type)) return false;
    if (direct.modelHandle && normalize(direct.modelHandle) !== normalize(record.handle)) return false;
    if (direct.model && !modelTextMatches(record, direct.model, direct.manufacturer || record.manufacturer)) return false;
    return true;
  };

  const modelProperties = (direct = {}) => {
    const exact = currentExact();
    if (exact && contextMatchesRecord(exact, direct)) {
      return {
        'Réplique exacte AW': String(exact.label || exact.model).slice(0, 180),
        '_AW Model': String(exact.label || exact.model).slice(0, 180),
        '_AW Model Handle': String(exact.handle).slice(0, 180),
        '_AW Model Status': 'exact'
      };
    }

    const replica = activeReplica();
    if (!replica?.id || !replica.model) return {};
    const unresolved = unresolvedRecord(replica);
    if (direct.platform && normalize(direct.platform) !== unresolved.platform) return {};
    if (direct.manufacturer && unresolved.manufacturer && normalize(direct.manufacturer) !== unresolved.manufacturer) return {};
    if (direct.type && unresolved.type && normalize(direct.type) !== unresolved.type) return {};
    return {
      '_AW Garage Model': String(replica.model).slice(0, 180),
      '_AW Model Status': 'unresolved'
    };
  };

  const decorateBody = (body, properties) => {
    if (!body || !properties || !Object.keys(properties).length) return body;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      Object.entries(properties).forEach(([key, value]) => {
        if (!body.get(`properties[${key}]`)) body.set(`properties[${key}]`, value);
      });
      return body;
    }
    if (typeof body !== 'string') return body;
    try {
      const parsed = JSON.parse(body);
      if (Array.isArray(parsed?.items)) {
        parsed.items = parsed.items.map((item) => ({
          ...item,
          properties: { ...properties, ...(item.properties || {}) }
        }));
      } else if (parsed && typeof parsed === 'object') {
        parsed.properties = { ...properties, ...(parsed.properties || {}) };
      }
      return JSON.stringify(parsed);
    } catch (_) { return body; }
  };

  const installFetchBridge = () => {
    if (!window.fetch || window.fetch.__awModelContextBridge === true) return false;
    const innerFetch = window.fetch.bind(window);
    const wrapped = (input, init = {}) => {
      let nextInit = init;
      try {
        const url = typeof input === 'string' ? input : (input?.url || '');
        const method = String(init?.method || input?.method || 'GET').toUpperCase();
        if (method === 'POST' && /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(url) && Object.prototype.hasOwnProperty.call(init, 'body')) {
          const direct = directBodyContext(init.body);
          const properties = modelProperties(direct);
          if (Object.keys(properties).length) nextInit = { ...init, body: decorateBody(init.body, properties) };
        }
      } catch (_) {}
      return innerFetch(input, nextInit);
    };
    wrapped.__awModelContextBridge = true;
    wrapped.__awModelContextInner = innerFetch;
    window.fetch = wrapped;
    return true;
  };

  const patchPackRoot = (root) => {
    const instance = root?.awCatalogCompatibility;
    if (!instance || instance.__awModelContextPatched === true || typeof instance.properties !== 'function') return false;
    const baseProperties = instance.properties.bind(instance);
    instance.properties = () => {
      const props = { ...baseProperties() };
      if (!props['_AW Replica ID']) return props;
      const replica = activeReplica();
      if (!replica?.id || String(replica.id) !== String(props['_AW Replica ID'])) return props;
      const canonical = resolveReplica(replica);
      if (canonical) {
        props['Réplique exacte AW'] = String(canonical.label || canonical.model).slice(0, 180);
        props['_AW Model'] = String(canonical.label || canonical.model).slice(0, 180);
        props['_AW Model Handle'] = String(canonical.handle).slice(0, 180);
        props['_AW Model Status'] = 'exact';
      } else if (replica.model) {
        props['_AW Garage Model'] = String(replica.model).slice(0, 180);
        props['_AW Model Status'] = 'unresolved';
      }
      return props;
    };
    instance.__awModelContextPatched = true;
    return true;
  };

  const patchPackRoots = () => {
    document.querySelectorAll('[data-aw-compatible-products]').forEach(patchPackRoot);
  };

  const modelMap = () => {
    const value = readJson(KEYS.modelMap, {});
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  };

  const savedProjects = () => {
    const value = readJson(KEYS.saved, []);
    return Array.isArray(value) ? value : [];
  };

  const canonicalForProject = (project, mapped) => {
    if (!project || !mapped) return null;
    const canonical = findModel(mapped.handle || mapped.label || mapped.model || '', {
      platform: project.platform || '',
      manufacturer: project.manufacturer || '',
      type: project.type || ''
    });
    return canonical && baseMatches(canonical, project) ? canonical : null;
  };

  const patchProjectLinks = () => {
    const root = document.querySelector('[data-aw-atelier]');
    if (!root) return;
    const projects = savedProjects();
    const map = modelMap();
    root.querySelectorAll('[data-aw-project]').forEach((card) => {
      const id = String(card.dataset.awProject || '');
      const project = projects.find((item) => String(item?.id || '') === id);
      const canonical = canonicalForProject(project, map[id]);
      const link = card.querySelector('.aw-atelier__project-actions a.button[href*="configurateur-airsoft"]');
      if (!link) return;
      try {
        const url = new URL(link.href, location.origin);
        if (canonical?.handle) url.searchParams.set('aw_model', canonical.handle);
        else url.searchParams.delete('aw_model');
        link.href = url.toString();
        card.dataset.awExactModel = canonical?.handle || '';
      } catch (_) {}
    });
  };

  const cleanupModelMap = () => {
    const projects = savedProjects();
    const ids = new Set(projects.map((item) => String(item?.id || '')).filter(Boolean));
    const map = modelMap();
    let changed = false;
    Object.keys(map).forEach((id) => {
      if (!ids.has(String(id))) { delete map[id]; changed = true; }
    });
    if (changed) writeJson(KEYS.modelMap, map);
  };

  const duplicateMappingAfter = (sourceId, beforeIds) => {
    window.setTimeout(() => {
      const projects = savedProjects();
      const newProjects = projects.filter((item) => item?.id && !beforeIds.has(String(item.id)));
      const map = modelMap();
      const source = map[String(sourceId || '')];
      if (source && newProjects.length === 1) {
        map[String(newProjects[0].id)] = { ...source, savedAt: new Date().toISOString() };
        writeJson(KEYS.modelMap, map);
      }
      cleanupModelMap();
      patchProjectLinks();
    }, 30);
  };

  const showAtelierNotice = (message) => {
    const notice = document.querySelector('[data-aw-atelier-notice]');
    if (!notice) return;
    notice.hidden = false;
    notice.classList.remove('is-error');
    notice.textContent = message;
  };

  const installAtelierTransferBridge = () => {
    const root = document.querySelector('[data-aw-atelier]');
    if (!root || root.dataset.awModelTransferBridge === 'true') return;
    root.dataset.awModelTransferBridge = 'true';

    const exportButton = root.querySelector('[data-aw-atelier-export]');
    exportButton?.addEventListener('click', (event) => {
      const map = modelMap();
      const projects = savedProjects();
      const relevant = Object.fromEntries(Object.entries(map).filter(([id, record]) => {
        const project = projects.find((item) => String(item?.id || '') === String(id));
        return Boolean(canonicalForProject(project, record));
      }));
      if (!Object.keys(relevant).length) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const history = readJson(root.dataset.historyKey || KEYS.history, []);
      const payload = {
        app: 'Assassin Workshop — Mon Atelier',
        version: 3,
        exportedAt: new Date().toISOString(),
        projects,
        history: Array.isArray(history) ? history : [],
        modelContexts: relevant
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mon-atelier-aw-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showAtelierNotice('Export Mon Atelier préparé avec les modèles exacts AW.');
    }, true);

    const importInput = root.querySelector('[data-aw-atelier-import]');
    importInput?.addEventListener('change', () => {
      const file = importInput.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        try {
          const payload = JSON.parse(String(reader.result || ''));
          if (!payload?.modelContexts || typeof payload.modelContexts !== 'object') return;
          window.setTimeout(() => {
            const projects = savedProjects();
            const current = modelMap();
            Object.entries(payload.modelContexts).forEach(([id, mapped]) => {
              const project = projects.find((item) => String(item?.id || '') === String(id));
              const canonical = canonicalForProject(project, mapped);
              if (canonical) current[String(id)] = { ...canonical, savedAt: new Date().toISOString() };
            });
            writeJson(KEYS.modelMap, current);
            cleanupModelMap();
            patchProjectLinks();
            showAtelierNotice('Les modèles exacts AW de la sauvegarde ont été restaurés.');
          }, 120);
        } catch (_) {}
      });
      reader.readAsText(file);
    }, true);
  };

  const patchOrderHistory = () => {
    const root = document.querySelector('[data-aw-atelier]');
    if (!root) return;
    const names = new Map();
    root.querySelectorAll('[data-aw-shopify-order]').forEach((order) => {
      const item = order.querySelector('[data-aw-shopify-order-item][data-model]');
      const label = String(item?.dataset.model || '').trim();
      if (!label) return;
      const configuration = String(item.dataset.configuration || '').trim();
      const name = configuration || `Commande ${String(order.dataset.orderName || 'AW')}`;
      if (!names.has(name)) names.set(name, label);
    });
    root.querySelectorAll('[data-aw-atelier-history] article').forEach((article) => {
      const name = String(article.querySelector('p')?.textContent || '').trim();
      const label = names.get(name);
      const span = article.querySelector('span');
      if (!label || !span || span.dataset.awModelHistory === label) return;
      if (!span.textContent.includes(label)) span.textContent += ` · Réplique ${label}`;
      span.dataset.awModelHistory = label;
    });
  };

  const refreshAtelier = () => {
    installAtelierTransferBridge();
    cleanupModelMap();
    patchProjectLinks();
    patchOrderHistory();
  };

  const refresh = () => {
    syncFromShared();
    syncContext();
    installFetchBridge();
    patchPackRoots();
    refreshAtelier();
  };

  document.addEventListener('click', (event) => {
    const duplicate = event.target.closest?.('[data-aw-project-duplicate]');
    if (duplicate) {
      const beforeIds = new Set(savedProjects().map((item) => String(item?.id || '')));
      duplicateMappingAfter(duplicate.dataset.awProjectDuplicate || '', beforeIds);
    }
    if (event.target.closest?.('[data-aw-project-delete]')) {
      window.setTimeout(() => { cleanupModelMap(); patchProjectLinks(); }, 40);
    }
  }, true);

  window.addEventListener('aw:garage-context-changed', () => queueMicrotask(syncContext));
  window.addEventListener('aw:garage-replica-selected', () => queueMicrotask(syncContext));
  window.addEventListener('aw:context-changed', () => queueMicrotask(() => {
    syncFromShared();
    patchProjectLinks();
  }));
  window.addEventListener('storage', (event) => {
    if ([KEYS.active, KEYS.replicas, KEYS.context, KEYS.saved, KEYS.modelMap].includes(event.key)) {
      window.setTimeout(refresh, 0);
    }
  });
  window.addEventListener('popstate', () => window.setTimeout(refresh, 0));
  window.addEventListener('aw:model-index-ready', () => window.setTimeout(refresh, 0));
  document.addEventListener('shopify:section:load', () => window.setTimeout(refresh, 0));

  const atelier = document.querySelector('[data-aw-atelier]');
  if (atelier && 'MutationObserver' in window) {
    let scheduled = false;
    const afterPaint = callback => requestAnimationFrame(() => setTimeout(callback, 0));
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      afterPaint(() => {
        scheduled = false;
        patchProjectLinks();
        patchOrderHistory();
        patchPackRoots();
      });
    }).observe(atelier, { childList: true, subtree: true });
  }

  window.AWModelContextBridge = {
    version: VERSION,
    syncContext,
    resolveReplica,
    currentExact,
    modelProperties,
    patchProjectLinks,
    patchOrderHistory,
    refresh,
    policy: 'garage-model-is-exact-only-after-canonical-unique-base-match'
  };

  refresh();
  [0, 100, 500, 1500].forEach((delay) => window.setTimeout(refresh, delay));
})();